import { createSelector } from '@reduxjs/toolkit'
import store, { AppDispatch, RootState } from '../../../../store'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'
import { SceneManager } from '../../../../visualization/sceneManager'
import { updateHistory } from '../../../../actions/history'
import { ActionType } from '../../../../reducers/history'
import { EventType } from '../../../../utils/EventEmitter'
import { editWall, EditWallPayload } from '../../../../actions/construction'
import { ConstructionTab } from '../../../../reducers/construction'
import { BuildingEditingTool } from '../../../../models/Construction'
import { validate as isValidUUID } from 'uuid'
import {
  isDoorMesh,
  isRoofMesh,
  isWallMesh,
  isWindowMesh,
} from '../../../../visualization/utils'
import { Mesh } from '@babylonjs/core'
import { useEditWallTool } from '../../../../visualization/tools/useEditWallTool'
import { useEditDoorTool } from '../../../../visualization/tools/useEditDoorTool'
import { useEditWindowTool } from '../../../../visualization/tools/useEditWindowTool'
import { useEditRoofTool } from '../../../../visualization/tools/useEditRoofTool'

const EditWall = ({ mesh }: { mesh: Mesh }) => {
  useEditWallTool(mesh)
  return null
}

const EditDoor = ({ mesh }: { mesh: Mesh }) => {
  useEditDoorTool(mesh)
  return null
}

const EditWindow = ({ mesh }: { mesh: Mesh }) => {
  useEditWindowTool(mesh)
  return null
}

const EditRoof = ({ mesh }: { mesh: Mesh }) => {
  useEditRoofTool(mesh)
  return null
}

const selectSelectedElementId = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (constructionTab, tool, elementsIds) => {
    const elementId = elementsIds[0]
    if (
      constructionTab !== ConstructionTab.Editor ||
      tool !== BuildingEditingTool.DirectSelect ||
      !elementId ||
      !isValidUUID(elementId)
    ) {
      return null
    }

    return elementId
  },
)

const EditElement = () => {
  const dispatch = useDispatch<AppDispatch>()

  const selectedElementId = useSelector(selectSelectedElementId)
  const selectedMesh = useMemo(() => {
    if (!selectedElementId) {
      return null
    }

    const scene = SceneManager.getInstance().getScene()
    const mesh = scene?.getMeshById(selectedElementId)
    const allowedMeshTypes = [isWallMesh, isWindowMesh, isDoorMesh, isRoofMesh]

    if (
      mesh &&
      mesh instanceof Mesh &&
      allowedMeshTypes.some((fn) => fn(mesh))
    ) {
      return mesh
    }

    return null
  }, [selectedElementId])

  // Leave for wall snapping
  // TODO: Remove
  useEffect(() => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    if (!eventEmitter) {
      return
    }

    const handleEditWall = (editWallPayload: EditWallPayload) => {
      dispatch(editWall(editWallPayload))

      const convertData = store.getState().construction.convertData
      dispatch(
        updateHistory({
          convertData,
          items: [
            {
              itemId: editWallPayload.id,
              actionType: ActionType.EDIT,
            },
          ],
        }),
      )
    }

    eventEmitter.on(EventType.BuildingEditWallElement, handleEditWall)

    return () => {
      eventEmitter.off(EventType.BuildingEditWallElement, handleEditWall)
    }
  }, [dispatch])

  if (!selectedMesh) {
    return null
  }

  return (
    <>
      {isWallMesh(selectedMesh) && <EditWall mesh={selectedMesh} />}

      {isDoorMesh(selectedMesh) && <EditDoor mesh={selectedMesh} />}

      {isWindowMesh(selectedMesh) && <EditWindow mesh={selectedMesh} />}

      {isRoofMesh(selectedMesh) && <EditRoof mesh={selectedMesh} />}
    </>
  )
}

export default EditElement
